/**
 * The authenticated `profiles.*` family as fixture data (W9 §C.5, §A.4).
 *
 * One own profile, seeded PRIVATE, plus four public ones carrying skills, availabilities and
 * timezones so `profiles.search`'s two new filters (§A.4 P2) have something to filter. Two
 * gates mirror the server exactly: `getPublic` on a private (or unknown) slug throws
 * `NotFoundError` — the same 404 either way, so a client can never distinguish "no such
 * profile" from "that profile exists but is not public" — and `update` throws `ConflictError`
 * when the requested slug collides with another profile's, AFTER the same normalisation
 * `Profile.NormalizeSlug` applies server-side (lower-case, non-alphanumeric → `-`, runs
 * collapsed, trimmed).
 */
import { ConflictError, NotFoundError } from "~/api/errors";
import type {
  AnswerTelemetry,
  CvDraft,
  CvDraftEntry,
  CvInterview,
  CvInterviewResult,
  CvInterviewState,
  PortfolioEntry,
  ProfileCv,
  ProfilePatch,
  ProfileSearchPage,
  ProfileSearchQuery,
  SkillLevel,
} from "~/api/types";
import { EMPTY_CV_DRAFT } from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");

interface Skill {
  id: string;
  name: string;
  level: SkillLevel;
  endorsementCount: number;
  projectsUsedCount: number;
}

interface ProfileRow {
  id: string;
  userId: string;
  slug: string;
  displayName: string;
  headline: string | null;
  bioShort: string | null;
  bioExtended: string | null;
  avatarUrl: string | null;
  timezone: string | null;
  availability: string | null;
  collaborationStyle: string | null;
  languages: string[];
  /** Internal gate — never presented as-is; a public read always presents `null`. */
  isPublic: boolean;
  skills: Skill[];
  portfolio: PortfolioEntry[] | null;
  /** The CV's copy of a published interview — null until the owner publishes one. */
  interview: CvInterviewResult | null;
  updatedAt: string;
}

/** Server rule, mirrored exactly (`Profile.NormalizeSlug`). */
export function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const SEED_ROWS: ProfileRow[] = [
  {
    id: "profile-own",
    userId: "user-1",
    slug: "ada-lovelace",
    displayName: "Ada Lovelace",
    headline: "Flow platform engineer",
    bioShort: "I build the flow engine.",
    bioExtended: null,
    avatarUrl: null,
    timezone: "Asia/Jerusalem",
    availability: "FullTime",
    collaborationStyle: "Async",
    languages: ["en"],
    isPublic: false,
    skills: [
      {
        id: "skill-1",
        name: "TypeScript",
        level: "expert",
        endorsementCount: 4,
        projectsUsedCount: 9,
      },
    ],
    portfolio: null,
    interview: null,
    updatedAt: new Date(BASE_TIME).toISOString(),
  },
  {
    id: "profile-2",
    userId: "user-2",
    slug: "grace-hopper",
    displayName: "Grace Hopper",
    headline: "Compiler and tooling",
    bioShort: "Turns instructions into machines.",
    bioExtended: null,
    avatarUrl: null,
    timezone: "America/New_York",
    availability: "PartTime",
    collaborationStyle: "Sync",
    languages: ["en"],
    isPublic: true,
    skills: [
      {
        id: "skill-2",
        name: "Compilers",
        level: "expert",
        endorsementCount: 12,
        projectsUsedCount: 20,
      },
    ],
    portfolio: [{ title: "COBOL runtime", role: "Lead", technologies: ["COBOL"] }],
    // One seeded interviewed profile, so a card's badge and the public CV's block have
    // something to render without anyone sitting an interview first.
    interview: {
      interviewId: "interview-seed",
      at: new Date(BASE_TIME - 5 * 86_400_000).toISOString(),
      profession: "Compiler engineer",
      years: 20,
      hr: {
        score: 78,
        band: "strong",
        note: "Handed the runtime over with its failure modes written down.",
      },
      tech: {
        score: 91,
        band: "exceptional",
        note: "Named the decisions inside the COBOL runtime and what they cost.",
      },
      cognitive: {
        score: 84,
        band: "strong",
        note: "Weighed portability against speed and said which she would give up.",
      },
      overall: { score: 84, band: "strong", note: null },
      summary:
        "A compiler engineer of twenty years who has shipped a language runtime and can account for the trade-offs inside it.",
      strengths: ["compiler internals", "writes down failure modes"],
      facts: { hoursPerWeek: "24" },
      model: "gemma4:12b",
      plan: "cv-interview-v1@1",
      minutes: 54,
      integrity: "verified",
      // The seeded profile sat the SCORED assessment, which is why it carries bands at all.
      scored: true,
    },
    updatedAt: new Date(BASE_TIME - 86_400_000).toISOString(),
  },
  {
    id: "profile-3",
    userId: "user-3",
    slug: "alan-turing",
    displayName: "Alan Turing",
    headline: "Formal methods",
    bioShort: "Machines that decide.",
    bioExtended: null,
    avatarUrl: null,
    timezone: "Europe/London",
    availability: "ProjectBased",
    collaborationStyle: "Hybrid",
    languages: ["en"],
    isPublic: true,
    skills: [
      {
        id: "skill-3",
        name: "Cryptography",
        level: "expert",
        endorsementCount: 9,
        projectsUsedCount: 5,
      },
    ],
    portfolio: null,
    interview: null,
    updatedAt: new Date(BASE_TIME - 2 * 86_400_000).toISOString(),
  },
  {
    id: "profile-4",
    userId: "user-4",
    slug: "margaret-hamilton",
    displayName: "Margaret Hamilton",
    headline: "Mission-critical software",
    bioShort: "Software that has to work.",
    bioExtended: null,
    avatarUrl: null,
    timezone: "America/New_York",
    availability: "Unavailable",
    collaborationStyle: "Async",
    languages: ["en"],
    isPublic: true,
    skills: [
      {
        id: "skill-4",
        name: "Systems",
        level: "expert",
        endorsementCount: 7,
        projectsUsedCount: 3,
      },
    ],
    portfolio: null,
    interview: null,
    updatedAt: new Date(BASE_TIME - 3 * 86_400_000).toISOString(),
  },
  {
    id: "profile-5",
    userId: "user-5",
    slug: "katherine-johnson",
    displayName: "Katherine Johnson",
    headline: "Trajectory analysis",
    bioShort: "Numbers that have to land.",
    bioExtended: null,
    avatarUrl: null,
    timezone: "Asia/Kolkata",
    availability: "FullTime",
    collaborationStyle: "Sync",
    languages: ["en"],
    isPublic: true,
    skills: [
      {
        id: "skill-5",
        name: "Numerical methods",
        level: "advanced",
        endorsementCount: 5,
        projectsUsedCount: 4,
      },
    ],
    portfolio: null,
    interview: null,
    updatedAt: new Date(BASE_TIME - 4 * 86_400_000).toISOString(),
  },
];

function present(row: ProfileRow, isOwner: boolean): ProfileCv {
  return {
    id: row.id,
    slug: row.slug,
    displayName: row.displayName,
    headline: row.headline,
    bioShort: row.bioShort,
    bioExtended: row.bioExtended,
    avatarUrl: row.avatarUrl,
    timezone: row.timezone,
    availability: row.availability,
    collaborationStyle: row.collaborationStyle,
    languages: row.languages,
    // A public read presents `visibility: null` and `isOwner: false` BY CONSTRUCTION — neither
    // may drive a badge on that path (§A.4).
    visibility: isOwner ? (row.isPublic ? "public" : "private") : null,
    isOwner,
    skills: row.skills.map((skill) => ({ ...skill })),
    portfolio: row.portfolio,
    updatedAt: row.updatedAt,
    userId: row.userId,
    // profiles.getPublic never presents `certified` (backend follow-up B9) — the fixture
    // mirrors the gap rather than inventing the field.
    certified: false,
    // The published interview IS presented on both reads — it is part of the CV.
    interview: row.interview,
  };
}

/**
 * The interview as a fixture: the SAME shape the server walks — a plan of sections, drilled
 * until an answer carries something, scored as each section closes — with the wording canned
 * instead of written by a model, and the scores derived from how specific the answers were.
 * Deterministic on purpose: a scene test asserts on the result, and a fixture that asked a model
 * would make that test a coin toss.
 */
const FAKE_PLAN = {
  slug: "cv-build-v1",
  name: "Build my CV",
  version: 1,
  origin: "canonical",
  targetMinutes: 40,
  maxTurns: 32,
  // The CV BUILDER, like the real default: every section scores NOTHING. Scored plans (the
  // job-interview module) are a different flow; the backend tests cover that path.
  sections: [
    { id: "who", name: "What you do", dimensions: [], maxTurns: 2, probe: null },
    { id: "story", name: "How you got here", dimensions: [], maxTurns: 2, probe: null },
    { id: "roles", name: "Where you have worked", dimensions: [], maxTurns: 2, probe: null },
    { id: "signature", name: "The work at the top", dimensions: [], maxTurns: 2, probe: null },
    { id: "skills", name: "What you work with", dimensions: [], maxTurns: 2, probe: null },
    { id: "credentials", name: "Training and languages", dimensions: [], maxTurns: 1, probe: null },
    { id: "terms", name: "Availability and terms", dimensions: [], maxTurns: 1, probe: null },
    { id: "close", name: "Anything the CV is missing", dimensions: [], maxTurns: 1, probe: null },
  ],
};

const FAKE_QUESTIONS: Record<string, { opening: string; drill: string; minChars: number }> = {
  who: {
    opening:
      "Let's write your CV. Start with the plain version: what do you do, and how many years have you been doing it?",
    drill: "What kind of work do people usually come to you for?",
    minChars: 40,
  },
  story: {
    opening: "How did you get to this work — where did you start, and what changed along the way?",
    drill: "What was the turn in that path that mattered most?",
    minChars: 120,
  },
  roles: {
    opening:
      "What were your last two or three roles or clients — who, roughly when, and what was yours to own?",
    drill: "For that one, when was it, and what did you own?",
    minChars: 150,
  },
  signature: {
    opening: "What is the one piece of work you would put at the top of your CV?",
    drill: "What came of it, in concrete terms?",
    minChars: 150,
  },
  skills: {
    opening: "What do you work with day to day, and which two or three would you be tested on?",
    drill: "Where were those actually used?",
    minChars: 80,
  },
  credentials: {
    opening:
      "What education, certifications or training should the CV list, and which languages do you work in?",
    drill: "Anything else worth listing?",
    minChars: 30,
  },
  terms: {
    opening:
      "How many hours a week could you commit, when could you start, and what do you charge?",
    drill: "What are you already booked on?",
    minChars: 30,
  },
  close: {
    opening: "What should your CV say that this conversation has not asked you about?",
    drill: "Anything else?",
    minChars: 20,
  },
};

interface FakeTurn {
  sectionId: string;
  sectionName: string;
  kind: string;
  question: string;
  answer: string;
  at: string;
  arrival: string;
  edited: boolean;
}

interface InterviewRow {
  id: string;
  userId: string;
  status: "active" | "paused" | "completed" | "abandoned";
  sectionIndex: number;
  sectionTurnCount: number;
  question: string;
  questionKind: string;
  probe: string | null;
  turns: FakeTurn[];
  sections: {
    id: string;
    name: string;
    scores: Record<string, number>;
    note: string | null;
    specificity: number;
    turnCount: number;
    closedAt: string;
  }[];
  facts: Record<string, string>;
  pins: { headline?: string | null; summary?: string | null };
  contributions: Record<
    string,
    { facts: Record<string, string>; entry: CvDraftEntry; headline: string | null }
  >;
  draft: CvDraft;
  findings: { code: string; detail: string; sectionId: string; at: string }[];
  result: CvInterviewResult | null;
  skills: { name: string; level: string; sectionId: string | null }[];
  startedAt: string;
  pausedAt: string | null;
  completedAt: string | null;
  publishedAt: string | null;
}

/** Specific answers score well, one-liners do not — the server's rubric, mechanised. */
function fakeScore(answers: string[]): number {
  if (answers.length === 0) {
    return 50;
  }
  const average = answers.reduce((sum, answer) => sum + answer.length, 0) / answers.length;
  return Math.max(10, Math.min(95, Math.round(20 + average / 4)));
}

function fakeBand(score: number): string {
  if (score >= 85) return "exceptional";
  if (score >= 70) return "strong";
  if (score >= 50) return "solid";
  return "emerging";
}

/** Mirrors `AnswerIntegrity.Read`: the act of answering, never the text of it. */
function fakeArrival(telemetry: AnswerTelemetry | undefined, length: number): string {
  if (!telemetry || (telemetry.keystrokes === 0 && telemetry.pasteCount === 0)) {
    return "unverified";
  }
  if (
    telemetry.pastedChars / Math.max(1, length) >= 0.6 ||
    telemetry.keystrokes / Math.max(1, length) < 0.5
  ) {
    return "pasted";
  }
  return telemetry.pasteCount > 0 ? "mixed" : "typed";
}

/** The facts a section is expected to yield, read off the answer the cheap way. */
function fakeFacts(sectionId: string, answer: string): Record<string, string> {
  const number = answer.match(/\d+/)?.[0];
  if (!number) {
    return {};
  }
  if (sectionId === "who") return { years: number };
  if (sectionId === "terms") return { hoursPerWeek: number, rateAmount: number, rateUnit: "day" };
  return {};
}

/** The server's fold, mirrored: facts and CV are DERIVED from the contributions, in order. */
function refold(row: InterviewRow): void {
  const facts: Record<string, string> = {};
  const entries: CvDraftEntry[] = [];
  let headline: string | null = null;
  let headlineFrom: string | null = null;

  for (const [sectionId, contribution] of Object.entries(row.contributions)) {
    Object.assign(facts, contribution.facts);
    entries.push(contribution.entry);
    if (contribution.headline) {
      headline = contribution.headline;
      headlineFrom = sectionId;
    }
  }

  row.facts = facts;
  // The person's own corrections go on last and always win.
  row.draft = {
    ...row.draft,
    headline,
    headlineFrom,
    summary: entries.length > 0 ? "Built from the conversation so far." : null,
    summaryFrom: entries[0]?.sectionId ?? null,
    entries,
    empty: entries.length === 0 && headline === null,
  };
  if (row.pins.headline) {
    row.draft.headline = row.pins.headline;
    row.draft.headlineFrom = null;
    row.draft.empty = false;
  }
  if (row.pins.summary) {
    row.draft.summary = row.pins.summary;
    row.draft.summaryFrom = null;
  }
  row.draft.pinned = Boolean(row.pins.headline || row.pins.summary);
}

/** The plan and its scripts are hand-written constants; an index outside them is a bug, said plainly. */
function sectionAt(index: number) {
  const section = FAKE_PLAN.sections[index];
  if (!section) throw new Error(`Interview section ${index} does not exist.`);
  return section;
}
function scriptFor(sectionId: string) {
  const script = FAKE_QUESTIONS[sectionId];
  if (!script) throw new Error(`No interview script for section ${sectionId}.`);
  return script;
}

function presentInterview(row: InterviewRow): CvInterview {
  const section = sectionAt(Math.min(row.sectionIndex, FAKE_PLAN.sections.length - 1));
  const arrivals: Record<string, number> = {};
  for (const turn of row.turns) {
    arrivals[turn.arrival] = (arrivals[turn.arrival] ?? 0) + 1;
  }
  const band =
    row.findings.length > 0 || row.turns.some((turn) => turn.arrival === "pasted")
      ? "flagged"
      : row.turns.length > 0 && row.turns.every((turn) => turn.arrival !== "unverified")
        ? "verified"
        : "unverified";

  return {
    id: row.id,
    status: row.status,
    plan: { slug: FAKE_PLAN.slug, name: FAKE_PLAN.name, version: 1, origin: "canonical" },
    sectionId: section.id,
    sectionName: section.name,
    sectionNumber: Math.min(row.sectionIndex + 1, FAKE_PLAN.sections.length),
    sectionCount: FAKE_PLAN.sections.length,
    sectionTurnCount: row.sectionTurnCount,
    turnCount: row.turns.length + (row.question ? 1 : 0),
    maxTurns: FAKE_PLAN.maxTurns,
    targetMinutes: FAKE_PLAN.targetMinutes,
    elapsedMinutes: row.turns.length * 2,
    question: row.question,
    questionKind: row.questionKind,
    probe: row.probe,
    probeSeconds: row.probe === "timed" ? 90 : null,
    askedAt: row.question ? new Date(BASE_TIME).toISOString() : null,
    maxAnswerLength: 4000,
    turns: row.turns.map((turn) => ({ ...turn })),
    sections: row.sections.map((closed) => ({ ...closed, scores: { ...closed.scores } })),
    facts: { ...row.facts },
    draft: { ...row.draft, entries: [...row.draft.entries], skills: [...row.draft.skills] },
    scored: row.sections.some((closed) => Object.keys(closed.scores).length > 0),
    profession: row.result?.profession ?? null,
    years: row.result?.years ?? null,
    scores: row.result
      ? {
          hr: row.result.hr,
          tech: row.result.tech,
          cognitive: row.result.cognitive,
          overall: row.result.overall,
        }
      : null,
    summary: row.result?.summary ?? null,
    strengths: row.result?.strengths ?? [],
    skills: row.skills.map((skill) => ({ ...skill })),
    integrity: { band, findings: row.findings.map((f) => ({ ...f })), arrivals },
    model: row.result?.model ?? "gemma4:12b",
    startedAt: row.startedAt,
    pausedAt: row.pausedAt,
    completedAt: row.completedAt,
    publishedAt: row.publishedAt,
  };
}

export interface FakeProfilesDeps {
  requireSession(): unknown;
  delay<T>(value: T): Promise<T>;
  viewerId(): string;
}

export function createFakeProfiles(deps: FakeProfilesDeps) {
  // Per-instance state, cloned from the seed. Module-level rows would leak one test's published
  // interview into the next test's "has not sat one yet" screen — and a fixture whose state
  // depends on test ORDER is a fixture that will lie eventually.
  const rows: ProfileRow[] = SEED_ROWS.map((row) => ({
    ...row,
    languages: [...row.languages],
    skills: row.skills.map((skill) => ({ ...skill })),
    portfolio: row.portfolio ? row.portfolio.map((entry) => ({ ...entry })) : null,
    interview: row.interview ? { ...row.interview, strengths: [...row.interview.strengths] } : null,
  }));
  const interviews: InterviewRow[] = [];

  const requireInterview = (id: string): InterviewRow => {
    const found = interviews.find((row) => row.id === id && row.userId === deps.viewerId());
    if (!found) {
      // Someone else's id answers exactly as an unknown one does — the server's own posture.
      throw new NotFoundError("No such interview");
    }
    return found;
  };

  const findOwnOrNull = (): ProfileRow | null =>
    rows.find((row) => row.userId === deps.viewerId()) ?? null;

  const requireOwn = (): ProfileRow => {
    const found = findOwnOrNull();
    if (!found) {
      throw new NotFoundError("No profile exists yet");
    }
    return found;
  };

  return {
    own: async (): Promise<ProfileCv> => {
      deps.requireSession();
      // "creates the profile on first read" — the server's own behaviour; the fixture always
      // has a seeded row for the viewer, so this simply reads it.
      return deps.delay(present(requireOwn(), true));
    },

    update: async (patch: ProfilePatch): Promise<ProfileCv> => {
      deps.requireSession();
      const own = requireOwn();
      if (patch.publicSlug) {
        const normalized = normalizeSlug(patch.publicSlug);
        const collision = rows.find((row) => row.id !== own.id && row.slug === normalized);
        if (collision) {
          throw new ConflictError("That profile URL is already taken.");
        }
        own.slug = normalized;
      }
      if (patch.displayName !== undefined) own.displayName = patch.displayName;
      if (patch.headline !== undefined) own.headline = patch.headline;
      if (patch.bioShort !== undefined) own.bioShort = patch.bioShort;
      if (patch.bioExtended !== undefined) own.bioExtended = patch.bioExtended;
      if (patch.avatarUrl !== undefined) own.avatarUrl = patch.avatarUrl;
      if (patch.timezone !== undefined) own.timezone = patch.timezone;
      if (patch.availability !== undefined) own.availability = patch.availability;
      if (patch.collaborationStyle !== undefined) own.collaborationStyle = patch.collaborationStyle;
      if (patch.languages !== undefined) own.languages = patch.languages;
      if (patch.visibility !== undefined) own.isPublic = patch.visibility === "public";
      own.updatedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(present(own, true));
    },

    upsertSkill: async (input: {
      name: string;
      level?: SkillLevel;
      projectsUsedCount?: number;
    }): Promise<ProfileCv> => {
      deps.requireSession();
      const own = requireOwn();
      const existing = own.skills.find(
        (skill) => skill.name.toLowerCase() === input.name.toLowerCase(),
      );
      if (existing) {
        existing.level = input.level ?? existing.level;
        existing.projectsUsedCount = input.projectsUsedCount ?? existing.projectsUsedCount;
      } else {
        own.skills.push({
          id: `skill-${own.skills.length + 1}-${own.id}`,
          name: input.name,
          level: input.level ?? "intermediate",
          endorsementCount: 0,
          projectsUsedCount: input.projectsUsedCount ?? 0,
        });
      }
      own.updatedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(present(own, true));
    },

    removeSkill: async (skillId: string): Promise<ProfileCv> => {
      deps.requireSession();
      const own = requireOwn();
      own.skills = own.skills.filter((skill) => skill.id !== skillId);
      own.updatedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(present(own, true));
    },

    setPortfolio: async (entries: PortfolioEntry[]): Promise<ProfileCv> => {
      deps.requireSession();
      const own = requireOwn();
      // An empty array clears the whole portfolio — never re-interpreted as "no change".
      own.portfolio = entries.length > 0 ? entries : null;
      own.updatedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(present(own, true));
    },

    exportCv: async (): Promise<{ format: "json"; exportedAt: string; cv: ProfileCv }> => {
      deps.requireSession();
      const own = requireOwn();
      return deps.delay({
        format: "json",
        exportedAt: new Date(BASE_TIME).toISOString(),
        cv: present(own, true),
      });
    },

    search: async (query: ProfileSearchQuery): Promise<ProfileSearchPage> => {
      const skills = query.skills?.map((s) => s.toLowerCase());
      const filtered = rows
        .filter((row) => row.isPublic)
        .filter(
          (row) =>
            !skills?.length ||
            row.skills.some((skill) => skills.includes(skill.name.toLowerCase())),
        )
        .filter((row) => !query.availability || row.availability === query.availability)
        .filter((row) => !query.timezone || row.timezone === query.timezone);
      const offset = query.offset ?? 0;
      const limit = query.limit && query.limit > 0 && query.limit <= 100 ? query.limit : 25;
      return {
        pagination: { offset, limit, total: filtered.length },
        data: filtered.slice(offset, offset + limit).map((row) => present(row, false)),
      };
    },

    interview: {
      get: async (): Promise<CvInterviewState> => {
        deps.requireSession();
        const mine = interviews.filter((row) => row.userId === deps.viewerId());
        const current =
          mine.find((row) => row.status === "active" || row.status === "paused") ??
          [...mine].reverse().find((row) => row.status === "completed") ??
          null;
        return deps.delay({
          enabled: true,
          model: "gemma4:12b",
          plan: FAKE_PLAN,
          interview: current ? presentInterview(current) : null,
        });
      },

      start: async (): Promise<CvInterview> => {
        deps.requireSession();
        for (const row of interviews) {
          if (
            row.userId === deps.viewerId() &&
            (row.status === "active" || row.status === "paused")
          ) {
            row.status = "abandoned";
          }
        }
        const first = sectionAt(0);
        const row: InterviewRow = {
          id: `interview-${interviews.length + 1}`,
          userId: deps.viewerId(),
          status: "active",
          sectionIndex: 0,
          sectionTurnCount: 1,
          question: scriptFor(first.id).opening,
          questionKind: "opening",
          probe: first.probe,
          turns: [],
          sections: [],
          facts: {},
          pins: {},
          contributions: {},
          draft: EMPTY_CV_DRAFT,
          findings: [],
          result: null,
          skills: [],
          startedAt: new Date(BASE_TIME).toISOString(),
          pausedAt: null,
          completedAt: null,
          publishedAt: null,
        };
        interviews.push(row);
        return deps.delay(presentInterview(row));
      },

      answer: async (input: {
        interviewId: string;
        text: string;
        telemetry?: AnswerTelemetry;
      }): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(input.interviewId);
        if (row.status === "paused") {
          row.status = "active";
          row.pausedAt = null;
        }
        const section = sectionAt(row.sectionIndex);
        const script = scriptFor(section.id);
        const answer = input.text.slice(0, 4000);

        row.turns.push({
          sectionId: section.id,
          sectionName: section.name,
          kind: row.questionKind,
          question: row.question,
          answer,
          at: new Date(BASE_TIME).toISOString(),
          arrival: fakeArrival(input.telemetry, answer.length),
          edited: false,
        });
        // One contribution per section, replaced as the section is re-read — the same shape the
        // server folds, so a corrected answer refolds here too.
        row.contributions[section.id] = {
          facts: fakeFacts(section.id, answer),
          entry: {
            title: section.name,
            role: null,
            period: null,
            description: answer.slice(0, 160),
            outcome: null,
            technologies: [],
            sectionId: section.id,
            sectionName: section.name,
          },
          headline: section.id === "who" ? answer.slice(0, 48) : null,
        };
        refold(row);

        // Drill on a thin answer while the section has budget — the director's rule, mirrored.
        if (answer.length < script.minChars && row.sectionTurnCount < section.maxTurns) {
          row.question = script.drill;
          row.questionKind = "drill";
          row.probe = null;
          row.sectionTurnCount += 1;
          return deps.delay(presentInterview(row));
        }

        // The section closes: score it from its own turns, then move on.
        const own = row.turns.filter((turn) => turn.sectionId === section.id);
        const scores: Record<string, number> = {};
        for (const dimension of section.dimensions) {
          scores[dimension] = fakeScore(own.map((turn) => turn.answer));
        }
        row.sections.push({
          id: section.id,
          name: section.name,
          scores,
          note: "Answered with specifics.",
          specificity: Math.min(100, own.reduce((sum, turn) => sum + turn.answer.length, 0) / 10),
          turnCount: own.length,
          closedAt: new Date(BASE_TIME).toISOString(),
        });

        row.sectionIndex += 1;
        row.sectionTurnCount = 1;
        const next = FAKE_PLAN.sections[row.sectionIndex];
        if (next) {
          row.question = scriptFor(next.id).opening;
          row.questionKind = "opening";
          row.probe = next.probe;
          return deps.delay(presentInterview(row));
        }

        // Every section closed: fold them into one reading.
        const dimension = (key: string): number => {
          const scored = row.sections.filter((closed) => closed.scores[key] !== undefined);
          return scored.length === 0
            ? 0
            : Math.round(
                scored.reduce((sum, closed) => sum + (closed.scores[key] ?? 0), 0) / scored.length,
              );
        };
        const hr = dimension("hr");
        const tech = dimension("tech");
        const cognitive = dimension("cognitive");
        const overall = Math.round((hr + tech + cognitive) / 3);
        row.status = "completed";
        row.question = "";
        row.probe = null;
        row.completedAt = new Date(BASE_TIME).toISOString();
        row.skills = [{ name: "Interviewing", level: "advanced", sectionId: "close" }];
        row.result = {
          interviewId: row.id,
          at: row.completedAt,
          profession: row.turns[0]?.answer.slice(0, 60) ?? "Freelancer",
          years: Number.parseInt(row.facts.years ?? "", 10) || null,
          hr: { score: hr, band: fakeBand(hr), note: "Said what they did when it went wrong." },
          tech: { score: tech, band: fakeBand(tech), note: "Named the decision and what it cost." },
          cognitive: {
            score: cognitive,
            band: fakeBand(cognitive),
            note: "Weighed both options before choosing.",
          },
          overall: { score: overall, band: fakeBand(overall), note: null },
          summary:
            "A freelancer who answered every section of the system interview in their own words.",
          strengths: ["answers with specifics"],
          facts: {},
          model: "gemma4:12b",
          plan: "cv-interview-v1@1",
          minutes: row.turns.length * 2,
          integrity: presentInterview(row).integrity.band,
          scored: row.sections.some((closed) => Object.keys(closed.scores).length > 0),
        };
        return deps.delay(presentInterview(row));
      },

      amend: async (input: {
        interviewId: string;
        turnIndex: number;
        text: string;
      }): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(input.interviewId);
        const turn = row.turns[input.turnIndex];
        if (!turn) {
          throw new NotFoundError("No such answer");
        }
        turn.answer = input.text.slice(0, 4000);
        turn.edited = true;

        // Only the amended section is re-read; everything else stands and is refolded.
        const own = row.turns.filter((t) => t.sectionId === turn.sectionId);
        row.contributions[turn.sectionId] = {
          facts: fakeFacts(turn.sectionId, turn.answer),
          entry: {
            title: turn.sectionName,
            role: null,
            period: null,
            description: own.at(-1)?.answer.slice(0, 160) ?? null,
            outcome: null,
            technologies: [],
            sectionId: turn.sectionId,
            sectionName: turn.sectionName,
          },
          headline: turn.sectionId === "who" ? turn.answer.slice(0, 48) : null,
        };
        refold(row);
        return deps.delay(presentInterview(row));
      },

      editDraft: async (input: {
        interviewId: string;
        field: string;
        value: string | null;
      }): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(input.interviewId);
        if (input.field === "headline" || input.field === "summary") {
          row.pins[input.field] = input.value;
        }
        refold(row);
        return deps.delay(presentInterview(row));
      },

      pause: async (interviewId: string): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(interviewId);
        if (row.status === "active") {
          row.status = "paused";
          row.pausedAt = new Date(BASE_TIME).toISOString();
        }
        return deps.delay(presentInterview(row));
      },

      resume: async (interviewId: string): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(interviewId);
        if (row.status === "paused") {
          row.status = "active";
          row.pausedAt = null;
        }
        return deps.delay(presentInterview(row));
      },

      publish: async (input: {
        interviewId: string;
        publish: boolean;
        acceptSkills?: boolean;
        publishFacts?: boolean;
        publishRate?: boolean;
        applyToCv?: boolean;
      }): Promise<{ profile: ProfileCv; interview: CvInterview }> => {
        deps.requireSession();
        const row = requireInterview(input.interviewId);
        const own = requireOwn();
        if (input.publish && row.result) {
          const facts: Record<string, string> = {};
          if (input.publishFacts !== false && row.facts.hoursPerWeek) {
            facts.hoursPerWeek = row.facts.hoursPerWeek;
          }
          // The rate is the one fact that never travels unless it was asked for.
          if (input.publishRate && row.facts.rateAmount) {
            facts.rateAmount = row.facts.rateAmount;
          }
          own.interview = { ...row.result, facts };
        } else {
          own.interview = null;
        }
        row.publishedAt = input.publish ? new Date(BASE_TIME).toISOString() : null;
        if (input.publish && input.acceptSkills) {
          for (const skill of row.skills) {
            if (!own.skills.some((existing) => existing.name === skill.name)) {
              own.skills.push({
                id: `skill-${own.skills.length + 1}-${own.id}`,
                name: skill.name,
                level: skill.level as SkillLevel,
                endorsementCount: 0,
                projectsUsedCount: 0,
              });
            }
          }
        }
        if (input.publish && input.applyToCv) {
          // Additive only, as the server is: what the person already wrote is never overwritten.
          own.headline = own.headline ?? row.draft.headline;
          own.bioShort = own.bioShort ?? row.draft.summary;
          const existing = own.portfolio ?? [];
          own.portfolio = [
            ...existing,
            ...row.draft.entries
              .filter((entry) => !existing.some((e) => e.title === entry.title))
              .map((entry) => ({
                title: entry.title ?? "",
                role: entry.role ?? undefined,
                description: entry.description ?? undefined,
              })),
          ];
        }
        own.updatedAt = new Date(BASE_TIME).toISOString();
        return deps.delay({ profile: present(own, true), interview: presentInterview(row) });
      },

      discard: async (interviewId: string): Promise<CvInterview> => {
        deps.requireSession();
        const row = requireInterview(interviewId);
        if (row.status === "active" || row.status === "paused") {
          row.status = "abandoned";
          row.question = "";
        }
        return deps.delay(presentInterview(row));
      },
    },

    /** Mirrors `profiles.getPublic` — a private profile and an unknown slug are the SAME 404. */
    getPublic: async (slug: string): Promise<ProfileCv> => {
      const normalized = normalizeSlug(slug);
      const found = rows.find((row) => row.slug === normalized && row.isPublic);
      if (!found) {
        throw new NotFoundError("Profile not found");
      }
      return present(found, false);
    },
  };
}
